// LiveKit (WebRTC voice / data-channel chat) field group for the interface
// editor drawer (agent.md interfaceFields split). Voice carries the 48000 Hz
// sample-rate field; chat carries the data-channel topic/pattern/signal
// fields.
import { docUrl } from "../../../lib/docLinks";
import { HelpTip, Stack, TextInput, Textarea } from "../../../ui";
import { IntegrationCredentialSelect } from "../../IntegrationCredentialSelect";
import type { InterfaceFieldsProps } from "../lib/connectionDraft";

export function LiveKitInterfaceFields({
  draft,
  onDraftChange,
}: InterfaceFieldsProps) {
  const isVoice = draft.interfaceKind === "livekit_voice";
  return (
    <Stack gap={4}>
      <TextInput
        label="LiveKit server URL"
        required
        type="text"
        value={draft.livekitServerUrl}
        onChange={(event) => {
          onDraftChange({
            ...draft,
            livekitServerUrl: event.currentTarget.value,
          });
        }}
      />
      <IntegrationCredentialSelect
        provider="livekit"
        value={draft.livekitCredentialsRef}
        onChange={(credentialsRef) => {
          onDraftChange({
            ...draft,
            livekitCredentialsRef: credentialsRef,
          });
        }}
      />
      <TextInput
        help={
          <HelpTip label="About the agent dispatch name">
            Enter the LiveKit agent dispatch name, not a participant identity.
          </HelpTip>
        }
        label="Agent dispatch name"
        required
        value={draft.livekitAgentName}
        onChange={(event) => {
          onDraftChange({
            ...draft,
            livekitAgentName: event.currentTarget.value,
          });
        }}
      />
      {isVoice ? (
        <TextInput
          label="WebRTC sample rate (Hz)"
          required
          type="number"
          value={draft.livekitSampleRateHz}
          onChange={(event) => {
            onDraftChange({
              ...draft,
              livekitSampleRateHz: event.currentTarget.value,
            });
          }}
        />
      ) : (
        <>
          <TextInput
            label="Data topic"
            required
            value={draft.livekitTopic}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                livekitTopic: event.currentTarget.value,
              });
            }}
          />
          <Textarea
            help={
              <HelpTip label="About the outbound pattern">
                {
                  "Enter a JSON object. Use {{text}} for the simulated persona message."
                }
              </HelpTip>
            }
            label="Outbound pattern"
            required
            rows={4}
            value={draft.livekitOutboundPattern}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                livekitOutboundPattern: event.currentTarget.value,
              });
            }}
          />
          <TextInput
            label="Inbound text path"
            value={draft.livekitInboundTextPath}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                livekitInboundTextPath: event.currentTarget.value,
              });
            }}
          />
          <TextInput
            help={
              <HelpTip label="About the inbound type field">
                Optional. Set this together with the allowed type values to
                filter incoming messages.
              </HelpTip>
            }
            label="Inbound type field"
            value={draft.livekitInboundTypeField}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                livekitInboundTypeField: event.currentTarget.value,
              });
            }}
          />
          <TextInput
            help={
              <HelpTip label="About inbound type values">
                Optional comma-separated list of allowed message types.
              </HelpTip>
            }
            label="Inbound type values"
            value={draft.livekitInboundTypeValues}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                livekitInboundTypeValues: event.currentTarget.value,
              });
            }}
          />
          <TextInput
            help={
              <HelpTip
                docHref={docUrl("guides/connect-livekit")}
                docLabel="Connect a LiveKit agent"
                label="About the echo filter"
              >
                {
                  "Inbound messages whose `Echo-filter field` equals `Echo-filter value` are dropped, so text your agent reflects back never enters the Transcript."
                }
              </HelpTip>
            }
            label="Echo-filter field"
            value={draft.livekitEchoField}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                livekitEchoField: event.currentTarget.value,
              });
            }}
          />
          <TextInput
            label="Echo-filter value"
            value={draft.livekitEchoEquals}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                livekitEchoEquals: event.currentTarget.value,
              });
            }}
          />
          <TextInput
            label="Agent-end field"
            required
            value={draft.livekitAgentEndField}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                livekitAgentEndField: event.currentTarget.value,
              });
            }}
          />
          <TextInput
            label="Agent-end value"
            required
            value={draft.livekitAgentEndEquals}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                livekitAgentEndEquals: event.currentTarget.value,
              });
            }}
          />
          <TextInput
            label="Maximum message bytes"
            required
            type="number"
            value={draft.livekitMaxMessageBytes}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                livekitMaxMessageBytes: event.currentTarget.value,
              });
            }}
          />
        </>
      )}
    </Stack>
  );
}
